import * as React from "React";
import { withRouter, RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import { useResources } from "contexts/resource-context";
import {
    CalendarState,
    CustomImage,
    ImageDetails,
    RubberBandItem,
    SpiralItem,
} from "burde";
import { actionCreators, getCalendar } from "page/calendar/store";
import {
    actionCreators as uiActionCreators,
    getRubberbandItem,
    getSpiralItem,
} from "ui/store";
import { CalendarType, MonthDividerNumberOfPages } from "svg/constants";
import * as styles from "./configuration-summary.css";
import { ApplicationState } from "store";
import { getComponentByName } from "../helper";
import {
    getCalendarMaxNumberOfExtraPages,
    getComponentRoute,
} from "page/calendar/helper";
import {
    getBackCoverImage,
    getCoverImage,
    getCustomBackImage,
    getCustomCoverImage,
    getSelectedLayout,
} from "burde-api-content/store";
import { SvgCalendarLayout } from "ui/constants";

type ConfigurationSummaryProps = {
    calendar: CalendarState;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiral: SpiralItem | null;
    rubberband: RubberBandItem | null;
    calendarLayout: SvgCalendarLayout | null;
} & typeof uiActionCreators &
    typeof actionCreators &
    RouteComponentProps;

const ConfigurationSummary: React.FC<ConfigurationSummaryProps> = (props) => {
    const resources = useResources();

    const onClick = (componentName: string) => {
        const component = getComponentByName(componentName);

        if (component !== null) {
            props.UpdateShowMainMenu(false);
            props.UpdateActiveNavigationComponent(component);

            const targetComponentRoute = getComponentRoute(
                component.route,
                props.calendar.calendarSize,
                props.calendar.calendarType,
            );

            if (props.location.pathname !== targetComponentRoute) {
                props.history.push(targetComponentRoute);
            }
        }
    };

    const coverSummary = () => {
        let coverImageUrl = null;

        if (props.customCoverImage !== null) {
            coverImageUrl = props.customCoverImage.url;
        } else if (props.coverImage) {
            coverImageUrl = props.coverImage.endpoints.preview;
        }

        const coverColor =
            props.calendar.coverColor !== null ? props.calendar.coverColor : "";

        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_GroupCoverPage"]}
                </span>

                <a
                    onClick={() => {
                        onClick("coverPage");
                    }}>
                    {resources["NavControls_CoverPage"]}

                    {coverImageUrl !== null ? (
                        <span
                            className={styles.colorImageBox}
                            style={{ backgroundImage: `url(${coverImageUrl})` }}
                        />
                    ) : (
                        <span
                            className={styles.colorImageBox}
                            style={{ backgroundColor: coverColor }}
                        />
                    )}
                </a>

                {props.calendar.coverTexts.map((text, index) => {
                    return (
                        <a
                            onClick={() => {
                                onClick("coverText");
                            }}
                            key={`cover-text-${index}`}>
                            {`${resources["NavControls_CoverText"]}: ${text.text}`}
                            <span
                                className={styles.colorImageBox}
                                style={{ backgroundColor: text.color }}
                            />
                        </a>
                    );
                })}
            </div>
        );
    };

    const ownerPageSummary = () => {
        const email = props.calendar.ownerPage.email || "";
        const name = props.calendar.ownerPage.name || "";
        const phoneNumber = props.calendar.ownerPage.phoneNumber || "";

        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_OwnerPage"]}
                </span>
                <a
                    onClick={() => {
                        onClick("ownerPage");
                    }}>
                    {`${resources["Owner_Page_Form_Email"]}: ${email}`}
                </a>
                <a
                    onClick={() => {
                        onClick("ownerPage");
                    }}>
                    {props.calendar.calendarType === CalendarType.Calendar
                        ? `${resources["Owner_Page_Form_Name"]}: ${name}`
                        : `${resources["Owner_Page_Form_Notebook_Name"]}: ${name}`}
                </a>
                <a
                    onClick={() => {
                        onClick("ownerPage");
                    }}>
                    {`${resources["Owner_Page_Form_PhoneNumber"]}: ${phoneNumber}`}
                </a>
            </div>
        );
    };

    const bindingSummary = () => {
        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_GroupBinding"]}
                </span>
                <a
                    onClick={() => {
                        onClick("coverPage");
                    }}>
                    {resources["NavControls_Spiral"]}
                    <span
                        className={styles.colorImageBox}
                        style={{
                            backgroundColor: props.spiral
                                ? props.spiral.hexColor
                                : "",
                        }}
                    />
                </a>
                <a
                    onClick={() => {
                        onClick("coverPage");
                    }}>
                    {resources["NavControls_Rubberband"]}
                    <span
                        className={styles.colorImageBox}
                        style={{
                            backgroundColor: props.rubberband
                                ? props.rubberband.hexColor
                                : "",
                        }}
                    />
                </a>
            </div>
        );
    };

    const backCoverSummary = () => {
        let backCoverImageUrl = null;

        if (props.customBackImage !== null) {
            backCoverImageUrl = props.customBackImage.url;
        } else if (props.backCoverImage) {
            backCoverImageUrl = props.backCoverImage.endpoints.preview;
        }

        const backCoverColor =
            props.calendar.backCoverColor !== null
                ? props.calendar.backCoverColor
                : "";

        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_GroupBackpage"]}
                </span>

                <a
                    onClick={() => {
                        onClick("backpage");
                    }}>
                    {resources["NavControls_ColorAndImage"]}

                    {backCoverImageUrl !== null ? (
                        <span
                            className={styles.colorImageBox}
                            style={{
                                backgroundImage: `url(${backCoverImageUrl})`,
                            }}
                        />
                    ) : (
                        <span
                            className={styles.colorImageBox}
                            style={{ backgroundColor: backCoverColor }}
                        />
                    )}
                </a>
            </div>
        );
    };

    const contentSummary = () => {
        const numberOfDayModules = props.calendar.dayModules.length;
        const numberOfWeekModules = props.calendar.weekModules.length;
        const numberOfPersonalDates = props.calendar.personalDateItems.length;

        const layoutHasDayModules =
            props.calendarLayout !== null &&
            props.calendarLayout.maxDayModules > 0;

        const layoutHasWeekModules =
            props.calendarLayout !== null &&
            props.calendarLayout.maxWeekModules > 0;

        const layoutName = props.calendarLayout
            ? props.calendarLayout.title
            : "";

        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_GroupContent"]}
                </span>

                <a
                    onClick={() => {
                        onClick("layout");
                    }}>
                    {resources["NavControls_Layout"]}
                    <span className={styles.textBox}>{layoutName}</span>
                </a>

                <a
                    onClick={() => {
                        onClick("frame");
                    }}>
                    {resources["NavControls_Frame"]}
                    <span
                        className={styles.colorImageBox}
                        style={{
                            backgroundColor:
                                props.calendar.borderColor !== null
                                    ? props.calendar.borderColor
                                    : "",
                        }}
                    />
                </a>

                {layoutHasDayModules ? (
                    <a
                        onClick={() => {
                            onClick("dayModules");
                        }}>
                        {resources["NavControls_DayModules"]}
                        <span className={styles.textBox}>
                            {numberOfDayModules}
                        </span>
                    </a>
                ) : (
                    <a className={styles.inactiveLink}>
                        {props.calendar.calendarType === CalendarType.Calendar
                            ? resources["NavControlsDayModules"]
                            : resources["Notebook_DayModules"]}
                        <span className={styles.textBox}>
                            {numberOfDayModules}
                        </span>
                    </a>
                )}

                {layoutHasWeekModules ? (
                    <a
                        onClick={() => {
                            onClick("weekModules");
                        }}>
                        {props.calendar.calendarType === CalendarType.Calendar
                            ? resources["NavControls_WeekModules"]
                            : resources["Notebook_WeekModules"]}
                        <span className={styles.textBox}>
                            {numberOfWeekModules}
                        </span>
                    </a>
                ) : (
                    <a className={styles.inactiveLink}>
                        {props.calendar.calendarType === CalendarType.Calendar
                            ? resources["NavControls_WeekModules"]
                            : resources["Notebook_WeekModules"]}
                        <span className={styles.textBox}>
                            {numberOfWeekModules}
                        </span>
                    </a>
                )}

                {props.calendar.calendarType !== CalendarType.Notebook && (
                    <a
                        onClick={() => {
                            onClick("personalDates");
                        }}>
                        {resources["NavControls_PersonalDates"]}
                        <span className={styles.textBox}>
                            {numberOfPersonalDates}
                        </span>
                    </a>
                )}
            </div>
        );
    };

    const otherSummary = () => {
        let numberOfEndPages = props.calendar.extraPages.length * 2;

        const calendarNumberOfExtraPages = getCalendarMaxNumberOfExtraPages(
            props.calendar.calendarType,
            props.calendar.calendarSize,
        );

        let numberOfLinesPages =
            calendarNumberOfExtraPages - props.calendar.extraPages.length * 2;

        if (props.calendar.monthDivider !== null) {
            numberOfLinesPages -= MonthDividerNumberOfPages;
        }

        return (
            <div className={styles.summarySection}>
                <span className={styles.summaryTitle}>
                    {resources["NavControls_GroupOther"]}
                </span>

                <a
                    onClick={() => {
                        onClick("extraPages");
                    }}>
                    {resources["NavControls_ExtraPages"]}
                    <span className={styles.textBox}>{numberOfEndPages}</span>
                </a>

                {props.calendar.calendarType !== CalendarType.Notebook && (
                    <>
                        <a
                            onClick={() => {
                                onClick("extraPages");
                            }}>
                            {resources["NavControls_Lined"]}
                            <span className={styles.textBox}>
                                {numberOfLinesPages}
                            </span>
                        </a>
                        <a
                            onClick={() => {
                                onClick("monthDivider");
                            }}>
                            {resources["NavControls_MonthDivider"]}
                            <span className={styles.textBox}>
                                {props.calendar.monthDivider !== null
                                    ? MonthDividerNumberOfPages
                                    : 0}
                            </span>
                        </a>
                    </>
                )}
                <a
                    onClick={() => {
                        onClick("summary");
                    }}>
                    {resources["NavControls_Summary"]}
                </a>
            </div>
        );
    };

    return (
        <div className={styles.summary}>
            {coverSummary()}
            {ownerPageSummary()}
            {bindingSummary()}
            {backCoverSummary()}
            {contentSummary()}
            {otherSummary()}
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    calendarLayout: getSelectedLayout(state),
    coverImage: getCoverImage(state),
    backCoverImage: getBackCoverImage(state),
    customCoverImage: getCustomCoverImage(state),
    customBackImage: getCustomBackImage(state),
    spiral: getSpiralItem(state),
    rubberband: getRubberbandItem(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ConfigurationSummary),
);
